import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, UserPlus, Lock } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function RegisterDialog({ open, onOpenChange, plan = "" }) {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (!name || !email || !password) return setError("Fyll inn navn, e-post og passord.");
    if (password.length < 8) return setError("Passordet må ha minst 8 tegn.");
    if (password !== confirm) return setError("Passordene er ikke like.");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { name, email, password });
      login(data.user);
      onOpenChange?.(false);
      navigate("/hjem");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="register-dialog">
        <DialogHeader>
          <DialogTitle className="font-headings text-2xl font-extrabold tracking-tight">Kom i gang med Murphy</DialogTitle>
          <DialogDescription>
            {plan ? `Du har valgt ${plan}-abonnement. ` : ""}Opprett konto med e-post og passord. Du verifiserer identiteten din (BankID eller admin) før du legger til selskap.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 py-1">
          <div>
            <Label htmlFor="reg-name" className="text-xs font-medium">Fullt navn</Label>
            <Input id="reg-name" data-testid="register-dialog-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 h-11" placeholder="Ola Nordmann" />
          </div>
          <div>
            <Label htmlFor="reg-email" className="text-xs font-medium">E-post</Label>
            <Input id="reg-email" data-testid="register-dialog-email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-11" placeholder="din@epost.no" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="reg-pw" className="text-xs font-medium">Passord</Label>
              <Input id="reg-pw" data-testid="register-dialog-password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-11" placeholder="min. 8 tegn" />
            </div>
            <div>
              <Label htmlFor="reg-confirm" className="text-xs font-medium">Bekreft</Label>
              <Input id="reg-confirm" data-testid="register-dialog-confirm" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5 h-11" placeholder="gjenta" />
            </div>
          </div>

          {error ? <p data-testid="register-dialog-error" className="text-sm text-destructive">{error}</p> : null}

          <Button type="submit" data-testid="register-dialog-submit" disabled={loading} className="h-11 w-full gap-2 text-sm font-semibold active:scale-[0.98]">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
            {loading ? "Oppretter …" : "Opprett konto"}
          </Button>
        </form>

        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            Har du allerede konto?{" "}
            <Link to="/login" data-testid="register-dialog-login-link" onClick={() => onOpenChange?.(false)} className="font-semibold text-primary hover:underline">Logg inn</Link>
          </span>
          <span className="flex items-center gap-1.5 text-xs"><Lock className="h-3 w-3" /> Data i Norge</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
